import { forwardRef, type AnchorHTMLAttributes, type ElementType, type ReactNode } from 'react';
import { Link as TanStackLink, type LinkProps as TanStackLinkProps } from '@tanstack/react-router';
import { cn } from '@/utils';
import Typography from '../Typography/Typography';
import type { TypographyVariant, TypographyVariantSizeMap } from '../Typography/Typography';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CommonLinkProps {
  /**
   * Typography variant. Defaults to `'body'`.
   * @see Typography for all valid variant/size combinations.
   */
  variant?: TypographyVariant;
  /**
   * Typography size for the selected variant. Defaults to `'medium'`
   * (valid for the default `'body'` variant).
   */
  size?: TypographyVariantSizeMap[TypographyVariant];
  className?: string;
  children?: ReactNode;
}

/** Internal navigation via TanStack Router (`to`, `params`, `search`, …). */
type InternalLinkProps = CommonLinkProps & TanStackLinkProps & { href?: never };

/** External / non-route links (e.g. `mailto:`, `https:`) rendered as a plain anchor. */
type ExternalLinkProps = CommonLinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'children'> & {
    href: string;
    to?: never;
  };

export type LinkProps = InternalLinkProps | ExternalLinkProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Primer `Link` component.
 *
 * A styled anchor wrapping a Primer `Typography` for consistent typographic
 * treatment; the text colour is always `interactive`.
 *
 * - **Internal navigation:** pass TanStack Router props (`to`, `params`, …) —
 *   renders TanStack Router's `<Link>`.
 * - **External / non-route links:** pass `href` (e.g. `mailto:`, `https:`) —
 *   renders a plain `<a>` (add `target`/`rel` as needed).
 *
 * @example
 * <Link to="/dashboard">Go to Dashboard</Link>
 * @example
 * <Link href="mailto:creators@influenster.com">creators@influenster.com</Link>
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { variant = 'body', size = 'medium', className, ...rest },
  ref
) {
  return (
    <Typography
      ref={ref}
      as={'href' in rest && rest.href ? 'a' : (TanStackLink as ElementType)}
      variant={variant as TypographyVariant}
      size={size as never}
      color="interactive"
      className={cn('no-underline hover:underline', className)}
      {...(rest as object)}
    />
  );
});

Link.displayName = 'Link';
